import { ee, ensureEarthEngineReady } from '../../services/earth-engine';
import * as MapUtils from '../../common/map';
import * as Metadata from '../../common/metadata';
import { generateBrBGColors, expandTransectToRectangle } from '../../common/utils';
import { getImageKey } from '../../app/components/visualization/imageCsv';
import { Actions as Acquisition } from '../../store/ducks/acquisition';
import { Actions as Results } from '../../store/ducks/results';
import { Actions as Map } from '../../store/ducks/map';
import { aoiGeometry } from '../../common/aoi';

export const SESSION_EXPORT_VERSION = 1;
export const SESSION_EXPORT_MODULE = 'shore_r';

const PENDING_BASELINE_STORAGE_KEY = 'shoreRSessionImportBaseline';
const PENDING_SHAPES_STORAGE_KEY = 'shoreRSessionImportShapes';

/**
 * @function buildMissionsFromImages
 * @description Regroups a flat image list (as found in `chosenImages`) back into the per-mission
 * `{name, shortname, dates: {[date]: cloudPercentage0to100}}` structure `state.acquisition.missions`
 * normally holds. Identical to Shoreline's own helper (`src/algorithms/shoreline/sessionExport.js`)
 * — ShoreR reuses the exact same acquisition wizard/`PeriodImageChooser`, so the same reshaping is
 * needed for its candidate table (built via `uniteMissionsDates(missions)`) to render non-empty.
 * @param {Array<Object>} images - Flat image entries (`chosenImages` shape).
 * **Called by:
 * @see {importSessionState}
 * @memberof module:ShoreR
 * @returns {Array<Object>} Per-mission grouped dates, ready for `uniteMissionsDates`.
 */
const buildMissionsFromImages = (images) => {
  const byName = {};
  images.forEach((img) => {
    if (!byName[img.name]) byName[img.name] = { name: img.name, shortname: img.shortname || img.name, dates: {} };
    byName[img.name].dates[img.date] = (img.content ?? 0) * 100;
  });
  return Object.values(byName);
};

/**
 * @function buildSessionExport
 * @description Bundles everything needed to redraw a completed ShoreR analysis without re-running
 * it. Like Shoreline, ShoreR's final transect/shoreline result is already-evaluated plain GeoJSON
 * by the time it lands in Redux (`imagery/saga/shoreR.js`'s `performShoreRAnalysisCommit` calls
 * `.getInfo()`/`.evaluate()` before dispatching) — no Earth Engine re-run needed to reconstruct it.
 * Deliberately NOT exported: `state.results.shoreRProjections` (the rate-projection/Bruun step) —
 * its confirmation state lives in several local booleans/refs (`projectionConfirmed`,
 * `projectionPolylinesRef`, etc.) that aren't wired to resync from Redux, so an imported session
 * lands the user right after analysis completion and lets them redo that step fresh, same scoping
 * call Intertidal made for its own Kriging step.
 * @param {Object} params
 * @param {Number} params.satelliteIndex - `state.acquisition.satelliteIndex`.
 * @param {Array<Array<Number>>} params.coordinates - AOI coordinates, `state.acquisition.coordinates`.
 * @param {Array<Object>} params.chosenImages - `state.results.chosenImages`.
 * @param {Array<Array<Number>>} params.baselineCoords - `state.results.baselineData.coords`.
 * @param {Object} params.shoreRData - `state.results.shoreRData` (`{transectData, shorelinesGeoJSON}`
 * — its third field, `shorelineData`, is a live `ee.List` never read anywhere and is dropped here).
 * **Called by:
 * @see {ProcessingShoreRPage} - ShoreR's result page, via the "export session for testing" icon.
 * @memberof module:ShoreR
 * @returns {Object} A plain, JSON-serializable session snapshot.
 */
export const buildSessionExport = ({ satelliteIndex, coordinates, chosenImages, baselineCoords, shoreRData }) => ({
  version: SESSION_EXPORT_VERSION,
  module: SESSION_EXPORT_MODULE,
  exportedAt: new Date().toISOString(),
  satelliteIndex,
  satelliteType: window.sessionStorage.getItem('typeSatellite') || 'TOA',
  cloudCoverage: Number(window.sessionStorage.getItem('acquisitionCloudCoverage')) || 0.05,
  coordinates,
  chosenImages: chosenImages || [],
  baselineCoords: baselineCoords || null,
  transectData: shoreRData?.transectData || null,
  shorelinesGeoJSON: shoreRData?.shorelinesGeoJSON || null,
});

/**
 * @function importSessionState
 * @description Rehydrates a session exported by `buildSessionExport` straight into
 * `state.acquisition`/`state.results`, and stashes the baseline/shoreline/transect data in
 * sessionStorage for `restorePendingBaseline`/`renderPendingImportedShapes` to turn into map
 * overlays once the result page's map actually exists. No Earth Engine calls are made along this
 * path — reconstructing `ee.Geometry` from already-evaluated coordinates is a purely local,
 * client-side graph build.
 * @param {Function} dispatch - Redux dispatch.
 * @param {Object} data - A session snapshot produced by `buildSessionExport`.
 * @see {@link buildSessionExport}
 * **Called by:
 * @see {NavigationBar} - via the standardized "import session for testing" icon's onImport.
 * @memberof module:ShoreR
 * @returns {void}
 */
export const importSessionState = async (dispatch, data) => {
  await ensureEarthEngineReady();
  dispatch(Acquisition.reset());
  const {
    satelliteIndex,
    satelliteType,
    cloudCoverage,
    coordinates,
    chosenImages,
    baselineCoords,
    transectData,
    shorelinesGeoJSON,
  } = data;

  dispatch(Acquisition.setSatellite(satelliteIndex, satelliteType || 'TOA'));
  dispatch(Acquisition.setAOI(null, coordinates, aoiGeometry(coordinates), null));

  dispatch(Acquisition.setAvailableDates(chosenImages, buildMissionsFromImages(chosenImages)));
  if (chosenImages.length > 0) {
    const sortedDates = [...chosenImages].map((img) => img.date).sort();
    dispatch(Acquisition.setPeriod(sortedDates[0], sortedDates[sortedDates.length - 1]));
  }

  window.sessionStorage.setItem('acquisitionCloudCoverage', cloudCoverage || 0.05);
  const selection = {};
  chosenImages.forEach((img) => {
    selection[getImageKey(img)] = true;
  });
  window.sessionStorage.setItem('acquisitionImageSelection', JSON.stringify(selection));

  dispatch(Results.push('chosenImages', chosenImages));

  if (baselineCoords && baselineCoords.length >= 2) {
    dispatch(Results.push('baselineData', { coords: baselineCoords, baseline: ee.Geometry.LineString(baselineCoords) }));
    window.sessionStorage.setItem(PENDING_BASELINE_STORAGE_KEY, JSON.stringify(baselineCoords));
  } else {
    window.sessionStorage.removeItem(PENDING_BASELINE_STORAGE_KEY);
  }

  if (transectData && shorelinesGeoJSON) {
    dispatch(Results.push('shoreRData', { transectData, shorelinesGeoJSON }));
    window.sessionStorage.setItem(
      PENDING_SHAPES_STORAGE_KEY,
      JSON.stringify({ transectData, shorelinesGeoJSON, imageCount: chosenImages.length }),
    );
  } else {
    window.sessionStorage.removeItem(PENDING_SHAPES_STORAGE_KEY);
  }
};

/**
 * @function restorePendingBaseline
 * @description If `importSessionState` left a baseline waiting (see `PENDING_BASELINE_STORAGE_KEY`'s
 * comment), draws it as a real `google.maps.Polyline` and returns a `{overlay}` object shaped
 * exactly like `handleDrawing`'s own `baseLine` state, then clears the pending flag. Returns `null`
 * (a no-op) if nothing is pending, so it's safe to call unconditionally.
 * @param {google.maps.Map} map - The now-ready map instance to draw onto.
 * @see {@link importSessionState}
 * **Called by:
 * @see {ProcessingShoreRPage} - ShoreR's result page, via `GoogleMap`'s `onLoad`.
 * @memberof module:ShoreR
 * @returns {{overlay: google.maps.Polyline}|null}
 */
export const restorePendingBaseline = (map) => {
  const raw = window.sessionStorage.getItem(PENDING_BASELINE_STORAGE_KEY);
  if (!raw) return null;
  window.sessionStorage.removeItem(PENDING_BASELINE_STORAGE_KEY);

  let coords;
  try {
    coords = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(coords) || coords.length < 2) return null;

  const overlay = new window.google.maps.Polyline({
    path: coords.map(([lng, lat]) => ({ lat, lng })),
    strokeColor: '#03d4f0ff',
    strokeOpacity: 1,
    strokeWeight: 3,
  });
  overlay.setMap(map);
  return { overlay };
};

/**
 * @function renderPendingImportedShapes
 * @description If `importSessionState` left shoreline/transect features waiting (see
 * `PENDING_SHAPES_STORAGE_KEY`'s comment), turns them into map shapes now — via `MapUtils.addShape`,
 * the same client-side helper the real analysis's own map layers ultimately render through — and
 * clears the pending flag. A no-op if nothing is pending. Colors mirror
 * `performShoreRAnalysisCommit`'s own choices (`generateBrBGColors` for shorelines, per-transect
 * Esteves-label colors) so an imported result looks the same as a freshly-computed one.
 * @param {Function} dispatch - Redux dispatch.
 * @see {@link importSessionState}
 * **Called by:
 * @see {ProcessingShoreRPage} - ShoreR's result page, via `GoogleMap`'s `onLoad`.
 * @memberof module:ShoreR
 * @returns {void}
 */
export const renderPendingImportedShapes = (dispatch) => {
  const raw = window.sessionStorage.getItem(PENDING_SHAPES_STORAGE_KEY);
  if (!raw) return;
  window.sessionStorage.removeItem(PENDING_SHAPES_STORAGE_KEY);

  const { transectData, shorelinesGeoJSON, imageCount } = JSON.parse(raw);

  dispatch(Map.removeShapeGroup('shoreRCoastlineData'));
  dispatch(Map.removeShapeGroup(Metadata.FeatureType.TRANSECT));

  const shorelineFeatures = shorelinesGeoJSON?.features || [];
  const shoreColors = generateBrBGColors(Math.max(imageCount, 1));
  const shorelineShapes = shorelineFeatures.map((feature, i) =>
    MapUtils.addShape(feature, shoreColors[i % shoreColors.length], 1, 'shoreRCoastlineData'),
  );
  dispatch(Map.addShapes(shorelineShapes, 'forms.map.shorelines', 'shoreRCoastlineData', shorelineFeatures));

  const transectFeatures = Array.isArray(transectData) ? transectData : transectData?.features || [];
  const transectShapes = transectFeatures.map((feature) => {
    const color = Metadata.ESTEVES_LABELS[feature.properties?.label]?.color || '#FF0000';
    return MapUtils.addShape(expandTransectToRectangle(feature), color, 1, Metadata.FeatureType.TRANSECT);
  });
  dispatch(Map.addShapes(transectShapes, 'forms.map.transects.title', Metadata.FeatureType.TRANSECT, transectFeatures));
};
